/**
 * Centralized authentication state management to prevent cache-related login issues
 */
import { logger } from "../../utils/logger";
import Cookies from "universal-cookie";

export interface AuthState {
  isAuthenticated: boolean;
  hasValidToken: boolean;
  hasValidRefreshToken: boolean;
  token?: string;
  refreshToken?: string;
}

export class AuthStateManager {
  private static instance: AuthStateManager;
  private cookies: Cookies;

  private constructor() {
    this.cookies = new Cookies();
  }

  public static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  /**
   * Get current authentication state
   */
  public getAuthState(): AuthState {
    const token = this.cookies.get("token");
    const refreshToken = this.cookies.get("token_secret");

    return {
      isAuthenticated: !!(token || refreshToken),
      hasValidToken: !!token,
      hasValidRefreshToken: !!refreshToken,
      token,
      refreshToken,
    };
  }

  /**
   * Set authentication state consistently across all storage mechanisms
   */
  public setAuthState(token: string, refreshToken: string): void {
    try {
      // Set cookies with proper expiration
      this.cookies.set("token", token, {
        path: "/",
        maxAge: 3600, // 1 hour
        secure: window.location.protocol === "https:",
        sameSite: "lax",
      });

      this.cookies.set("token_secret", refreshToken, {
        path: "/",
        secure: window.location.protocol === "https:",
        sameSite: "lax",
      });

      // Note: Removed localStorage dependency - using cookies only for authentication

      logger.info("Authentication state set successfully");
    } catch (error) {
      logger.error("Failed to set authentication state:", error);
      throw error;
    }
  }

  /**
   * Clear authentication state from all storage mechanisms
   */
  public clearAuthState(): void {
    try {
      // Clear cookies
      this.cookies.remove("token", { path: "/" });
      this.cookies.remove("token_secret", { path: "/" });

      // Clear localStorage (keeping for backward compatibility)
      localStorage.removeItem("loggedIn");
      localStorage.removeItem("token");
      localStorage.removeItem("token_secret");
      localStorage.removeItem("tokenRefreshing");

      logger.info("Authentication state cleared successfully");
    } catch (error) {
      logger.error("Failed to clear authentication state:", error);
    }
  }

  /**
   * Validate authentication state consistency
   */
  public validateAuthState(): { isValid: boolean; issues: string[] } {
    const state = this.getAuthState();
    const issues: string[] = [];

    // Check for inconsistent states
    if (
      state.isAuthenticated &&
      !state.hasValidToken &&
      !state.hasValidRefreshToken
    ) {
      issues.push("Authenticated but no valid tokens found");
    }

    if (state.hasValidToken && !state.isAuthenticated) {
      issues.push("Valid token but not marked as authenticated");
    }

    if (state.hasValidRefreshToken && !state.isAuthenticated) {
      issues.push("Valid refresh token but not marked as authenticated");
    }

    // Note: Removed localStorage validation - using cookies only for authentication

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Fix authentication state inconsistencies
   */
  public fixAuthStateInconsistencies(): boolean {
    const validation = this.validateAuthState();

    if (validation.isValid) {
      return true;
    }

    logger.warn(
      "Authentication state inconsistencies detected:",
      validation.issues
    );

    // If we have valid tokens but inconsistent state, fix it
    const state = this.getAuthState();
    if (state.hasValidToken || state.hasValidRefreshToken) {
      // Re-sync the state
      if (state.token && state.refreshToken) {
        this.setAuthState(state.token, state.refreshToken);
        return true;
      }
    }

    // If state is completely inconsistent, clear everything
    this.clearAuthState();
    return false;
  }

  /**
   * Check if user should be redirected to login
   */
  public shouldRedirectToLogin(): boolean {
    const state = this.getAuthState();

    // If we have no authentication at all, redirect to login
    if (
      !state.isAuthenticated &&
      !state.hasValidToken &&
      !state.hasValidRefreshToken
    ) {
      return true;
    }

    // If we have inconsistent state, try to fix it first
    const validation = this.validateAuthState();
    if (!validation.isValid) {
      const fixed = this.fixAuthStateInconsistencies();
      if (!fixed) {
        return true;
      }
    }

    return false;
  }
}

// Export singleton instance
export const authStateManager = AuthStateManager.getInstance();
